import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { PatTableSelectorPageConfig } from '@front-platform/content-builder';
import { patTableSelectorPageResolveConfig } from '@front-platform/content-builder';
import { Component, inject, OnInit, input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DoodOnSiteLocationModel } from '@core/models/on-site-location.model';
import { OnSiteLocationsService } from '@core/services/on-site-locations/on-site-locations.service';
import { OnSiteLocationStoreDispatcher } from '@common/dispatchers/on-site-location.dispatcher';
import { DoodOnSiteLocationSourceValues } from '@store/on-site-location/on-site-location.enum';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { NavigationHistoryService } from '@core/services/navigation-history/navigation-history.service';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
export interface PatTableSelectorPageRuntimeConfig {}

export type PatTableSelectorPageConfigInput = Partial<
  Omit<PatTableSelectorPageConfig, keyof PatTableSelectorPageRuntimeConfig> &
    PatTableSelectorPageRuntimeConfig
>;
export type PatTableSelectorPageResolvedConfig = Omit<
  PatTableSelectorPageConfig,
  keyof PatTableSelectorPageRuntimeConfig
> &
  Partial<PatTableSelectorPageRuntimeConfig>;
export interface PatTableSelectorPageContract extends ConfigComponentContract<
  PatTableSelectorPageConfigInput,
  PatTableSelectorPageResolvedConfig
> {}

@Component({
  selector: 'app-pat-table-selector-page',
  template: `
    <div class="fixed inset-0 flex flex-col overflow-hidden pt-5 pb-page-bottom">
      @if (isLoading) {
        <div class="flex flex-1 items-center justify-center">
          <img
            class="h-10 w-10 animate-spin"
            src="assets/images/spinner.svg"
            alt="Loading"
          />
        </div>
      }

      @if (!isLoading && !shopId) {
        <div
          class="flex flex-1 items-center justify-center px-5 text-center text-xl font-bold text-neutral-700"
        >
          Shop inconnu
        </div>
      }

      @if (!isLoading && shopId) {
        <!-- Affichage liste -->
        <div class="flex flex-1 flex-col overflow-y-auto px-5 pb-3">
          @if (currentTab === 'keyboard') {
            <div class="mt-auto space-y-3">
              @for (table of filteredTables; track table) {
                <div
                  (click)="selectTable(table.id)"
                  class="cursor-pointer rounded bg-neutral-50 px-4 py-4 shadow-sm transition-colors active:bg-neutral-300"
                >
                  <div
                    class="text-l flex flex-row items-center justify-between gap-4 text-neutral-800"
                  >
                    {{ table.name }}
                    <i class="icon icon-arrow-right icon-16"></i>
                  </div>
                </div>
              }
            </div>
          }
          @if (currentTab === 'all') {
            <div class="space-y-3 pt-2">
              @for (table of sortedOnSiteLocations; track table) {
                <div
                  (click)="selectTable(table.id)"
                  class="cursor-pointer rounded bg-neutral-50 px-4 py-4 shadow-sm transition-colors active:bg-neutral-300"
                >
                  <div
                    class="text-l flex flex-row items-center justify-between gap-4 text-neutral-800"
                  >
                    {{ table.name }}
                    <i class="icon icon-arrow-right icon-16"></i>
                  </div>
                </div>
              }
            </div>
          }
        </div>
        <!-- Bloc fixe clavier + onglets -->
        <div class="sticky bottom-0 w-full bg-white">
          @if (currentTab === 'keyboard') {
            <div class="px-5 shadow-inner">
              <div class="py-3 text-center font-brand text-5xl font-bold text-neutral-800">
                {{ input || '–' }}
              </div>
              <div class="grid grid-cols-3 gap-2 pb-4">
                @for (key of keypadLayout; track key) {
                  <button
                    (click)="handleKeyPress(key)"
                    [ngClass]="{
                      'bg-yellow-200 text-2xl text-yellow-800': key === '<',
                      'bg-red-200 text-red-800 text-xs': key === 'EFFACER',
                      'bg-neutral-50 text-neutral-800 text-3xl': key !== '<' && key !== 'EFFACER',
                    }"
                    class="h-16 rounded shadow-sm transition-all duration-100 active:scale-95 active:bg-neutral-300"
                  >
                    {{ key }}
                  </button>
                }
              </div>
            </div>
          }
          <div class="px-5 py-4 shadow-inner">
            <div class="flex space-x-3">
              <button
                class="flex-1 rounded-full border border-neutral-300 bg-neutral-100 py-2 text-center text-sm font-bold text-neutral-700"
                [class.bg-primary-600]="currentTab === 'keyboard'"
                [class.text-white]="currentTab === 'keyboard'"
                (click)="setTab('keyboard')"
              >
                {{ 'pay-at-table.select-table.tab-keyboard' | translate }}
              </button>
              <button
                class="flex-1 rounded-full border border-neutral-300 bg-neutral-100 py-2 text-center text-sm font-bold text-neutral-700"
                [class.bg-primary-600]="currentTab === 'all'"
                [class.text-white]="currentTab === 'all'"
                (click)="setTab('all')"
              >
                {{ 'pay-at-table.select-table.tab-all' | translate }}
              </button>
            </div>
          </div>
        </div>
      }
    </div>
  `,
  styles: [],
  standalone: false,
})
export class PatTableSelectorPageComponent implements OnInit, PatTableSelectorPageContract {
  readonly config = input<PatTableSelectorPageConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    PatTableSelectorPageConfig,
    PatTableSelectorPageRuntimeConfig
  >(patTableSelectorPageResolveConfig, () => ({ config: this.config() }));
  onSiteLocations: DoodOnSiteLocationModel[] = [];
  input = '';
  keypadLayout: string[] = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '<', '0', 'EFFACER'];
  isLoading = true;
  currentTab: 'keyboard' | 'all' = 'keyboard';
  shopId: string | null = null;

  private router = inject(Router);
  private onSiteLocationsService = inject(OnSiteLocationsService);
  private onSiteLocationDispatcher = inject(OnSiteLocationStoreDispatcher);
  private routerHelper = inject(RouterHelperService);
  private navigationHistoryService = inject(NavigationHistoryService);
  private activatedRoute = inject(ActivatedRoute);
  private settingsSelector = inject(SettingsStoreSelector);

  constructor() {
    this.navigationHistoryService.pushStateFromActivatedRouteSnapshot(
      'home',
      this.activatedRoute.snapshot,
    );
  }

  ngOnInit(): void {
    this.shopId =
      this.activatedRoute.snapshot.queryParamMap.get('shop') || localStorage.getItem('shopId');

    if (!this.shopId) {
      this.isLoading = false;
      return;
    }

    localStorage.setItem('shopId', this.shopId);

    // Clé de cache localStorage spécifique au shop
    const cacheKey = `onsiteLocations_${this.shopId}`;
    const cached = localStorage.getItem(cacheKey);

    if (cached) {
      try {
        const tableNameCollator = this.createTableNameCollator();
        const parsed: DoodOnSiteLocationModel[] = JSON.parse(cached);
        this.onSiteLocations = parsed
          .filter(loc => !!loc.name)
          .sort((a, b) => tableNameCollator.compare(a.name, b.name));
        this.isLoading = false; // afficher les données tout de suite

        // Check for automatic table selection after cached data is loaded
        this.handleAutomaticTableSelection();
      } catch (e) {
        console.warn('Erreur lors du parsing du cache onsiteLocations', e);
      }
    }

    // Mise à jour des données depuis l’API dans tous les cas
    this.onSiteLocationsService.loadOnSiteLocations$(this.shopId).subscribe(locations => {
      const tableNameCollator = this.createTableNameCollator();
      const validLocations = locations
        .map(location => ({ ...location }))
        .filter(loc => !!loc.name)
        .sort((a, b) => tableNameCollator.compare(a.name, b.name));

      this.onSiteLocations = validLocations;

      try {
        localStorage.setItem(cacheKey, JSON.stringify(validLocations));
      } catch (e) {
        console.warn('Erreur lors de l’enregistrement du cache onsiteLocations', e);
      }

      this.isLoading = false;

      // Check for automatic table selection after data is loaded
      this.handleAutomaticTableSelection();
    });
  }

  get filteredTables(): DoodOnSiteLocationModel[] {
    return this.onSiteLocations.filter(loc => loc.name.includes(this.input));
  }

  get sortedOnSiteLocations(): DoodOnSiteLocationModel[] {
    return [...this.onSiteLocations];
  }

  handleKeyPress(key: string): void {
    if (key === 'EFFACER') {
      this.clearInput();
    } else if (key === '<') {
      this.input = this.input.slice(0, -1);
    } else {
      this.input += key;
    }
  }

  clearInput(): void {
    this.input = '';
  }

  setTab(tab: 'keyboard' | 'all'): void {
    this.currentTab = tab;
    if (tab === 'all') {
      this.clearInput();
    }
  }

  selectTable(id: string): void {
    this.onSiteLocationDispatcher.updateSettings({
      id,
      lastLocationId: id,
      source: DoodOnSiteLocationSourceValues.fromUser,
    });
    this.onSiteLocationDispatcher.updateActive(id);
    this.router.navigate([this.routerHelper.translateRoute('/pay-at-table/overview')]);
  }

  private handleAutomaticTableSelection(): void {
    const onSiteLocationId = this.activatedRoute.snapshot.queryParamMap.get('onSiteLocationId');
    // const onSiteLocationName = this.route.snapshot.queryParamMap.get('onSiteLocationName');
    const timestampParam = this.activatedRoute.snapshot.queryParamMap.get('timestamp');

    if (onSiteLocationId && timestampParam) {
      const timestamp = parseInt(timestampParam, 10);
      const currentTime = Date.now();
      const timeDifference = currentTime - timestamp;

      // Check if timestamp is less than 1 minute (60000 ms)
      if (timeDifference < 60000 && timeDifference > 0) {
        // Find the table with the matching ID
        const targetTable = this.onSiteLocations.find(table => table.id === onSiteLocationId);

        if (targetTable) {
          console.log('Auto-selecting table:', targetTable.name, 'with ID:', onSiteLocationId);

          // Automatically select the table and redirect
          this.selectTable(onSiteLocationId);
          return;
        } else {
          console.warn('Table with ID', onSiteLocationId, 'not found in available tables');
        }
      } else {
        console.warn('Timestamp is too old or invalid:', timeDifference, 'ms ago');
      }
    }
  }

  private createTableNameCollator(): Intl.Collator {
    return new Intl.Collator(this.settingsSelector.app.locale || 'fr', {
      numeric: true,
      sensitivity: 'base',
    });
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(PatTableSelectorPageComponent, { 'keyAliases': {} });
